import React, { useCallback, useMemo } from 'react';

import makeStyles from '@mui/styles/makeStyles';

import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { FormattedMessage, useIntl } from 'react-intl';

import { useHistory } from 'react-router';
import { DateTime } from 'luxon';
import { useGetMyAnalyticsData } from 'modules/Insights/Notes/hooks/useGetMyAnalyticsData';
import { MyAnalyticsDimensionType } from 'graphql/types';
import { useNotesFilterContext } from 'modules/notes/NotesFilterContextProvider';
import { useNoteInsightsContext } from 'modules/Insights/Notes/NoteInsightsContextProvider';
import { Legend } from '../Common/Legend';
import { bubbleColorSet, transformAnalyticsDatatoBubble } from './utils';
import { BubbleChart } from './BubbleChart';
import BubbleChartNoData from './BubbleChartNoData';
import BubbleChartSkeleton from './BubbleChartSkeleton';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    padding: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  heading: {
    color: theme.palette.text.secondary
  },

  chartContainer: {
    paddingTop: theme.spacing(1)
  },
  legendContainer: {
    marginTop: theme.spacing(1.5),
    marginLeft: theme.spacing(-1)
  }
}));

export const KeywordCloudChart: React.FC = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const classes = useStyles();
  const {
    filterContext: { searchCriteria, ...rest },
    setFilterContext
  } = useNotesFilterContext();
  const { data, loading, isEmpty } = useGetMyAnalyticsData(
    MyAnalyticsDimensionType.MetricsByKeywords
  );
  const { currentPeriod } = useNoteInsightsContext();

  const tranformData = useMemo(() => {
    if (isEmpty) {
      return { dataSet: [], legendSet: [] };
    } else return transformAnalyticsDatatoBubble(data?.myAnalytics?.items);
  }, [data?.myAnalytics?.items, isEmpty]);

  const sortedLegend = useMemo(() => {
    const legend = tranformData.legendSet.map((i: string) => {
      return { key: i, value: formatMessage({ id: `labelType.${i}` }) };
    });
    legend.sort((a, b) => {
      return a.value.toLowerCase().localeCompare(b.value.toLowerCase());
    });
    return legend;
  }, [formatMessage, tranformData.legendSet]);

  const handleClick = useCallback(
    (target, chart) => {
      const criteria = {
        displayText: chart.data.label,
        id: chart.data.id,
        key: chart.data.label,
        label: chart.data.label,
        labelId: chart.data.labelId,
        value: chart.data.id
      };

      const searchKey = formatMessage({
        id: `labelType.${chart.data.type}`
      }).toLowerCase();

      const dateSelector = [
        {
          key: `start+${currentPeriod?.startDate}`,
          value: DateTime.fromISO(currentPeriod?.startDate || '')
        },
        {
          key: `end+${currentPeriod?.endDate}`,
          value: DateTime.fromISO(currentPeriod?.endDate || '')
        }
      ];

      const criteriaObj = {
        [`${searchKey}:`]: [criteria],
        ['dateRange:']: dateSelector
      };

      const searchCreteriaObj = {
        ...searchCriteria,
        ...{ criterions: { ...searchCriteria.criterions, ...criteriaObj } }
      };

      setFilterContext({
        searchCriteria: searchCreteriaObj,
        ...rest
      });
      history.push('/my-work');
    },
    [
      currentPeriod?.endDate,
      currentPeriod?.startDate,
      formatMessage,
      history,
      rest,
      searchCriteria,
      setFilterContext
    ]
  );

  return (
    <Paper
      className={classes.container}
      elevation={3}
      data-testid={'keyword-cloud-chart-container'}
      tabIndex={0}
    >
      <Grid container>
        <Grid container item>
          <Grid
            item
            xs={12}
            container
            alignItems={'center'}
            justifyContent="space-between"
            data-testid="keyword_cloud_label"
          >
            <Typography variant="h6" className={classes.heading}>
              <FormattedMessage id="insights.keywordCloud.title" />
            </Typography>
          </Grid>
        </Grid>
        {loading ? (
          <>
            <BubbleChartSkeleton />
          </>
        ) : (
          <>
            {!isEmpty && (
              <>
                <Grid
                  container
                  item
                  xs={12}
                  className={classes.legendContainer}
                  justifyContent="flex-end"
                >
                  {sortedLegend.map((i) => (
                    <Legend
                      key={i.key}
                      label={i.value}
                      color={bubbleColorSet[i.key]}
                      borderColor={bubbleColorSet[i.key]}
                      borderStyle="soild"
                      labelColor={bubbleColorSet[i.key]}
                    />
                  ))}
                </Grid>
                <Grid
                  container
                  item
                  className={classes.chartContainer}
                  data-testid={'keyword-cloud'}
                >
                  <BubbleChart
                    data={tranformData.dataSet}
                    onClick={handleClick}
                  />
                </Grid>
              </>
            )}
            {isEmpty && (
              <>
                <BubbleChartNoData />
              </>
            )}
          </>
        )}
      </Grid>
    </Paper>
  );
};
